// ══════════════════════════════════════════════════════════════════════════════
// COCKPIT PRO - Espace professionnel pour les artisans et vendeurs
// Design identique au Cockpit Particulier avec les espaces métier Pro
// DESKTOP: Interface unifiée style Claude (menu + chat IA + artifacts)
// MOBILE: Layout classique avec bottom navigation
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, Package, PlusCircle, MessageCircle, Heart, Euro, ShoppingBag,
  Users, User, Settings, ChevronRight, ChevronDown, Bell, TrendingUp, Eye, Clock,
  CheckCircle, AlertTriangle, BarChart3, Wallet, Star, Share2, LogOut,
  Menu, X, Briefcase, Store, Megaphone, Building2, Handshake, Shield, Link2,
  FileText, Send, Wrench, Image, BookOpen, Search, Bookmark, UserPlus, Newspaper,
  Calendar, Hash, Target, PieChart, Truck, HardHat, Zap, Mail, Globe, Copy, Check, 
  CreditCard, Receipt, UserCog, Building, Palette, Layers, BadgeCheck,
  Network, BarChart2, Percent, Gift, ShoppingCart, Folder, Tag, Camera,
  Mic, Sparkles, PanelRightOpen, FolderOpen, FileCode, Presentation,
  Plus, ImageIcon, Film, Paperclip, File, Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import PublishProModal from '../../components/publish/PublishProModal';
import { NotificationBell, NotificationsPanel } from '../../components/pro/NotificationsComponents';
import NotificationsBell from '../../components/notifications/NotificationsBell';
import { AbracaCoinsBadge } from '../../components/credits';
import VoiceNotesSuperpouvoir from '../../components/ai/VoiceNotesSuperpouvoir';
import ArtifactCard from '../../components/artifacts/ArtifactCard';
import ArtifactViewer from '../../components/artifacts/ArtifactViewer';
import { AbraNotificationContainer } from '../../components/mascot';
import { ThemeToggle } from '../../context/ThemeContext';
import { API_URL } from '../../config/api';
import CockpitAssistantUnified from '../cockpit/CockpitAssistantUnified';
import CockpitProTopNav from '../../components/layout/CockpitProTopNav';

// ══════════════════════════════════════════════════════════════════════════════
// CONFIGURATION DES ESPACES PRO
// ══════════════════════════════════════════════════════════════════════════════

const ESPACES_PRO_CONFIG = {
  collaboration: {
    id: 'collaboration',
    label: 'Collaboration',
    icon: Users,
    color: 'blue',
    notifications: 0,
    subItems: [
      { id: 'col-dashboard', path: '/cockpit-pro', label: 'Tableau de bord', icon: Home },
      { id: 'col-equipe', path: '/cockpit-pro/collaboration/equipe', label: 'Mon Équipe', icon: Users },
      { id: 'col-collabs', path: '/cockpit-pro/collaboration/collabs', label: 'Collabs (Externes)', icon: UserPlus },
      { id: 'col-pointage', path: '/cockpit-pro/collaboration/pointage', label: 'Pointage RH', icon: Clock },
      { id: 'col-taches', path: '/cockpit-pro/collaboration/taches', label: 'Tâches partagées', icon: CheckCircle },
      { id: 'col-agenda', path: '/cockpit-pro/collaboration/agenda', label: 'Agenda partagé', icon: Calendar },
      { id: 'col-tchat', path: '/cockpit-pro/collaboration/tchat', label: 'Tchat équipe', icon: MessageCircle },
      { id: 'col-feed', path: '/cockpit-pro/collaboration/feed', label: 'Feed équipe', icon: Newspaper },
      { id: 'col-documents', path: '/cockpit-pro/collaboration/documents', label: 'Documents partagés', icon: Folder },
    ]
  },
  commercial: {
    id: 'commercial',
    label: 'Commercial',
    icon: Target,
    color: 'orange',
    notifications: 0,
    subItems: [
      { id: 'com-dashboard', path: '/cockpit-pro/commercial', label: 'Tableau de bord', icon: Home },
      { id: 'com-demandes', path: '/cockpit-pro/commercial/demandes-devis', label: 'Demandes de devis', icon: FileText },
      { id: 'com-prospects', path: '/cockpit-pro/commercial/prospects', label: 'Prospects', icon: UserPlus },
      { id: 'com-pipeline', path: '/cockpit-pro/commercial/pipeline', label: 'Pipeline', icon: TrendingUp },
      { id: 'com-clients', path: '/cockpit-pro/commercial/clients', label: 'Clients', icon: Users },
      { id: 'com-devis', path: '/cockpit-pro/commercial/devis', label: 'Mes Devis', icon: Receipt },
      { id: 'com-factures', path: '/cockpit-pro/commercial/factures', label: 'Factures', icon: Receipt },
      { id: 'com-relances', path: '/cockpit-pro/commercial/relances', label: 'Relances', icon: Bell },
    ]
  },
  catalogue: {
    id: 'catalogue',
    label: 'Catalogue',
    icon: Layers,
    color: 'blue',
    notifications: 0,
    subItems: [
      { id: 'cat-dashboard', path: '/cockpit-pro/catalogue', label: 'Vue d\'ensemble', icon: Home },
      { id: 'cat-produits', path: '/cockpit-pro/catalogue/produits', label: 'Mes Produits', icon: Package },
      { id: 'cat-prestations', path: '/cockpit-pro/catalogue/prestations', label: 'Mes Prestations', icon: Wrench },
      { id: 'cat-tarifs', path: '/cockpit-pro/catalogue/tarifs', label: 'Grille tarifaire', icon: Euro },
      { id: 'cat-import', path: '/cockpit-pro/catalogue/import', label: 'Import CSV', icon: FileText },
    ]
  },
  chantiers: {
    id: 'chantiers',
    label: 'Chantiers',
    icon: HardHat,
    color: 'slate',
    notifications: 0,
    subItems: [
      { id: 'ch-dashboard', path: '/cockpit-pro/chantiers', label: 'Tableau de bord', icon: Home },
      { id: 'ch-encours', path: '/cockpit-pro/chantiers/en-cours', label: 'Chantiers en cours', icon: Wrench },
      { id: 'ch-avenir', path: '/cockpit-pro/chantiers/a-venir', label: 'À venir', icon: Calendar },
      { id: 'ch-termines', path: '/cockpit-pro/chantiers/termines', label: 'Terminés', icon: CheckCircle },
      { id: 'ch-planning', path: '/cockpit-pro/chantiers/planning', label: 'Planning', icon: Calendar },
      { id: 'ch-pointage', path: '/cockpit-pro/chantiers/pointage', label: 'Pointage GPS', icon: Target },
    ]
  },
  marketing: {
    id: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    color: 'orange',
    notifications: 0,
    subItems: [
      { id: 'mk-dashboard', path: '/cockpit-pro/marketing', label: 'Tableau de bord', icon: Home },
      { id: 'mk-stats', path: '/cockpit-pro/marketing/statistiques', label: 'Statistiques', icon: BarChart2 },
      { id: 'mk-campagnes', path: '/cockpit-pro/marketing/campagnes', label: 'Campagnes email', icon: Mail },
      { id: 'mk-promos', path: '/cockpit-pro/marketing/promotions', label: 'Promotions', icon: Percent },
      { id: 'mk-ventes-flash', path: '/cockpit-pro/marketing/ventes-flash', label: 'Ventes Flash', icon: Zap },
    ]
  },
  reseauSocial: {
    id: 'reseauSocial',
    label: 'Réseau Social',
    icon: Globe,
    color: 'blue',
    notifications: 0,
    subItems: [
      { id: 'rs-feed', path: '/cockpit-pro/reseau-social', label: 'Mon Feed', icon: Newspaper },
      { id: 'rs-posts', path: '/cockpit-pro/reseau-social/posts', label: 'Mes Publications', icon: FileText },
      { id: 'rs-galerie', path: '/cockpit-pro/reseau-social/galerie', label: 'Ma Galerie', icon: Image },
      { id: 'rs-abonnes', path: '/cockpit-pro/reseau-social/abonnes', label: 'Mes Abonnés', icon: Users },
      { id: 'rs-messages', path: '/cockpit-pro/reseau-social/messages', label: 'Messages', icon: MessageCircle },
    ]
  },
  reseauxSociaux: {
    id: 'reseauxSociaux',
    label: 'Réseaux Sociaux',
    icon: Share2,
    color: 'blue',
    notifications: 0,
    subItems: [
      { id: 'rso-hub', path: '/cockpit-pro/reseaux-sociaux', label: 'Mes Réseaux', icon: Link2 },
      { id: 'rso-publications', path: '/cockpit-pro/reseaux-sociaux/publications', label: 'Publications', icon: Newspaper },
      { id: 'rso-messages', path: '/cockpit-pro/reseaux-sociaux/messages', label: 'Messages', icon: MessageCircle },
      { id: 'rso-stats', path: '/cockpit-pro/reseaux-sociaux/statistiques', label: 'Statistiques', icon: BarChart3 },
    ]
  },
  reseauPro: {
    id: 'reseauPro',
    label: 'Réseau Pro',
    icon: Network,
    color: 'slate',
    notifications: 0,
    isComingSoon: true,
    comingSoonDescription: 'Un réseau dédié uniquement aux professionnels du bâtiment. Connecte-toi avec d\'autres artisans, partage tes réalisations, trouve des partenaires.',
    subItems: []
  },
  centraleAchat: {
    id: 'centraleAchat',
    label: 'Centrale d\'achat',
    icon: ShoppingCart,
    color: 'slate',
    notifications: 0,
    isComingSoon: true,
    comingSoonDescription: 'Centrale d\'achats et regroupements d\'achats pour les professionnels du bâtiment. Prix négociés sur les matériaux et équipements.',
    subItems: []
  },
  emploi: {
    id: 'emploi',
    label: 'Emploi',
    icon: Briefcase,
    color: 'orange',
    notifications: 0,
    subItems: [
      { id: 'em-recherche', path: '/cockpit-pro/emploi', label: 'Rechercher', icon: Search },
      { id: 'em-offres', path: '/cockpit-pro/emploi/mes-offres', label: 'Mes Offres', icon: FileText },
      { id: 'em-candidatures', path: '/cockpit-pro/emploi/candidatures', label: 'Candidatures', icon: Users },
    ]
  },
  boutique: {
    id: 'boutique',
    label: 'Ma Boutique',
    icon: Store,
    color: 'orange',
    notifications: 0,
    subItems: [
      { id: 'bt-apercu', path: '/cockpit-pro/boutique', label: 'Aperçu', icon: Eye },
      { id: 'bt-infos', path: '/cockpit-pro/boutique/informations', label: 'Informations', icon: Building },
      { id: 'bt-visuels', path: '/cockpit-pro/boutique/visuels', label: 'Logo & Couverture', icon: Camera },
      { id: 'bt-apropos', path: '/cockpit-pro/boutique/a-propos', label: 'À propos', icon: FileText },
      { id: 'bt-mentions', path: '/cockpit-pro/boutique/mentions-legales', label: 'Mentions Devis', icon: Receipt },
      { id: 'bt-docs', path: '/cockpit-pro/boutique/documents-legaux', label: 'Documents Légaux', icon: Shield },
      { id: 'bt-horaires', path: '/cockpit-pro/boutique/horaires', label: 'Horaires', icon: Clock },
      { id: 'bt-blog', path: '/cockpit-pro/boutique/blog', label: 'Blog & Articles', icon: BookOpen },
      { id: 'bt-paiements', path: '/cockpit-pro/paiements', label: 'Paiements Stripe', icon: CreditCard },
    ]
  },
  admin: {
    id: 'admin',
    label: 'Administration',
    icon: Settings,
    color: 'slate',
    notifications: 0,
    subItems: [
      { id: 'ad-compte', path: '/cockpit-pro/admin', label: 'Mon Compte', icon: User },
      { id: 'ad-abonnement', path: '/cockpit-pro/admin/abonnement', label: 'Abonnement', icon: CreditCard },
      { id: 'ad-facturation', path: '/cockpit-pro/admin/facturation', label: 'Facturation', icon: Receipt },
      { id: 'ad-securite', path: '/cockpit-pro/admin/securite', label: 'Sécurité', icon: Shield },
      { id: 'ad-notifications', path: '/cockpit-pro/admin/notifications', label: 'Notifications', icon: Bell },
    ]
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // SUPERADMIN ONLY - Gestion de la plateforme AbracadaWorld
  // ═══════════════════════════════════════════════════════════════════════════
  plateforme: {
    id: 'plateforme',
    label: '👑 Plateforme',
    icon: Shield,
    color: 'orange',
    notifications: 0,
    superadminOnly: true,
    subItems: [
      { id: 'pf-dashboard', path: '/cockpit-pro/plateforme', label: 'Vue d\'ensemble', icon: BarChart3 },
      { id: 'pf-cercle-confiance', path: '/cockpit-pro/plateforme/cercle-confiance', label: 'Cercle de Confiance', icon: BadgeCheck },
      { id: 'pf-abonnements', path: '/cockpit-pro/plateforme/abonnements', label: 'Abonnements', icon: CreditCard },
      { id: 'pf-vendeurs', path: '/cockpit-pro/plateforme/vendeurs', label: 'Vendeurs / Boutiques', icon: Store },
      { id: 'pf-utilisateurs', path: '/cockpit-pro/plateforme/utilisateurs', label: 'Utilisateurs', icon: Users },
      { id: 'pf-analytics', path: '/cockpit-pro/plateforme/analytics', label: 'Analytics', icon: TrendingUp },
      { id: 'pf-moderation', path: '/cockpit-pro/plateforme/moderation', label: 'Modération', icon: Shield },
      { id: 'pf-tickets', path: '/cockpit-pro/plateforme/tickets', label: 'Tickets Support', icon: MessageCircle },
      { id: 'pf-factures', path: '/cockpit-pro/plateforme/factures', label: 'Facturation Globale', icon: Receipt },
      { id: 'pf-parametres', path: '/cockpit-pro/plateforme/parametres', label: 'Paramètres', icon: Settings },
    ]
  },
};

// Couleurs par espace - Charte Abracadabâti
const ESPACE_COLORS = {
  orange: { bg: 'bg-[#FFF8F4]', text: 'text-[#F97316]', activeBg: 'bg-[#FEF3E8]', border: 'border-[#F97316]/20', iconBg: 'bg-orange-100' },
  blue: { bg: 'bg-[#F0F4F8]', text: 'text-[#1E3A5F]', activeBg: 'bg-[#E8EEF4]', border: 'border-[#1E3A5F]/20', iconBg: 'bg-blue-100' },
  slate: { bg: 'bg-slate-50', text: 'text-slate-600', activeBg: 'bg-slate-100', border: 'border-slate-200', iconBg: 'bg-slate-100' },
};

// ══════════════════════════════════════════════════════════════════════════════
// ABONNEMENTS
// ══════════════════════════════════════════════════════════════════════════════

const ABONNEMENTS = {
  artisan: { name: 'Artisan', price: 49, maxProducts: 100, maxTeam: 1 },
  pro: { name: 'Pro', price: 99, maxProducts: 1000, maxTeam: 5 },
  business: { name: 'Business', price: 149, maxProducts: 10000, maxTeam: 10 },
};

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANTS
// ══════════════════════════════════════════════════════════════════════════════

// Composant pour un item d'espace avec sous-menu dépliable
function EspaceMenuItem({ espace, isExpanded, onToggle, onClose, location, onShowComingSoon }) {
  const colors = ESPACE_COLORS[espace.color];
  const Icon = espace.icon;
  
  // Si c'est un espace "à venir", afficher un style grisé
  if (espace.isComingSoon) {
    return (
      <div className="mb-1">
        <button
          onClick={() => onShowComingSoon(espace)}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-gray-400 hover:bg-gray-50 cursor-pointer"
        >
          <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">
            <Icon size={16} className="text-gray-400" />
          </div>
          <span className="flex-1 text-left">{espace.label}</span>
          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-600 text-[10px] font-bold rounded uppercase">
            À venir
          </span>
        </button>
      </div>
    );
  }
  
  const hasActiveChild = espace.subItems.some(item => 
    location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  );

  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className={`
          w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all
          ${isExpanded || hasActiveChild ? `${colors.bg} ${colors.text}` : 'text-gray-700 hover:bg-gray-100'}
        `}
      >
        <div className={`w-7 h-7 rounded-lg ${colors.iconBg} flex items-center justify-center`}>
          <Icon size={16} className={isExpanded || hasActiveChild ? colors.text : 'text-gray-500'} />
        </div>
        <span className="flex-1 text-left">{espace.label}</span>
        {espace.notifications > 0 && (
          <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full shadow-sm">
            {espace.notifications}
          </span>
        )}
        <ChevronDown 
          size={16} 
          className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
        />
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className={`ml-4 mt-1 pl-3 border-l-2 ${colors.border} space-y-0.5`}>
              {espace.subItems.map((item) => {
                const ItemIcon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    onClick={onClose}
                    className={`
                      flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all
                      ${isActive 
                        ? `${colors.activeBg} ${colors.text} font-medium` 
                        : 'text-gray-600 hover:bg-gray-50'
                      }
                    `}
                  >
                    <ItemIcon size={16} className={isActive ? colors.text : 'text-gray-400'} />
                    <span className="flex-1">{item.label}</span>
                    {item.badge > 0 && (
                      <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                        isActive ? 'bg-white/50' : 'bg-orange-500 text-white'
                      }`}>
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Sidebar du Cockpit Pro
function CockpitProSidebar({ isOpen, onClose, userProfile, subscription, onLogout, onOpenVoiceNotes, userRole }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [expandedEspaces, setExpandedEspaces] = useState(['collaboration']);
  const [comingSoonModal, setComingSoonModal] = useState(null);
  
  // Vérifier si l'utilisateur est superadmin
  const isSuperAdmin = userRole === 'superadmin';

  useEffect(() => {
    Object.entries(ESPACES_PRO_CONFIG).forEach(([key, espace]) => {
      if (espace.isComingSoon) return; // Skip coming soon espaces
      if (espace.superadminOnly && !isSuperAdmin) return; // Skip superadmin-only si pas superadmin
      const hasActiveChild = espace.subItems.some(item => 
        location.pathname === item.path || location.pathname.startsWith(item.path + '/')
      );
      if (hasActiveChild && !expandedEspaces.includes(key)) {
        setExpandedEspaces(prev => [...prev, key]);
      }
    });
  }, [location.pathname, isSuperAdmin]);

  const toggleEspace = (espaceId) => {
    setExpandedEspaces(prev => 
      prev.includes(espaceId) 
        ? prev.filter(id => id !== espaceId)
        : [...prev, espaceId]
    );
  };

  const subscriptionInfo = ABONNEMENTS[subscription] || ABONNEMENTS.artisan;

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-[10200] lg:hidden"
          onClick={onClose}
        />
      )}

      <aside className={`
        fixed lg:sticky top-0 left-0 z-[10201] lg:z-auto
        w-full lg:w-72 h-full lg:h-screen bg-white border-r border-gray-200
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col
      `}
      style={{ 
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}
      >
        {/* Header */}
        <div className="p-4 pt-3 border-b border-gray-200 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a]">
          <div className="flex items-center justify-between">
            <Link to="/pro" className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                <Building2 size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">Cockpit Pro</h1>
                <p className="text-xs text-white/70">{subscriptionInfo.name} • {subscriptionInfo.price}€/mois</p>
              </div>
            </Link>
            <button 
              onClick={onClose}
              className="lg:hidden p-3 hover:bg-white/20 rounded-xl text-white touch-manipulation"
              style={{ minWidth: '48px', minHeight: '48px' }}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* User Info avec Badge Pro */}
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] rounded-full flex items-center justify-center text-white font-bold text-lg">
                {userProfile?.initials || 'BP'}
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#F97316] rounded-full flex items-center justify-center">
                <BadgeCheck size={12} className="text-white" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{userProfile?.name || 'BatiPro Services'}</p>
              <p className="text-xs text-gray-500">{userProfile?.metier || 'Plombier • Électricien'}</p>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex items-center gap-1">
                  <Star size={12} className="text-amber-500 fill-amber-500" />
                  <span className="text-xs font-medium text-gray-700">{userProfile?.rating || '4.9'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Shield size={12} className="text-[#1E3A5F]" />
                  <span className="text-xs font-medium text-[#1E3A5F]">Vérifié</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Limites abonnement */}
          <div className="mt-3 p-2 bg-white rounded-lg border border-gray-200">
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">Produits</span>
              <span className="font-medium text-[#1E3A5F]">{userProfile?.productCount || 0} / {subscriptionInfo.maxProducts}</span>
            </div>
            <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#F97316] rounded-full transition-all"
                style={{ width: `${Math.min(100, ((userProfile?.productCount || 0) / subscriptionInfo.maxProducts) * 100)}%` }}
              />
            </div>
            {subscriptionInfo.maxTeam > 1 && (
              <>
                <div className="flex items-center justify-between text-xs mt-2">
                  <span className="text-gray-500">Équipe</span>
                  <span className="font-medium text-[#1E3A5F]">{userProfile?.teamCount || 1} / {subscriptionInfo.maxTeam}</span>
                </div>
                <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#1E3A5F] rounded-full transition-all"
                    style={{ width: `${Math.min(100, ((userProfile?.teamCount || 1) / subscriptionInfo.maxTeam) * 100)}%` }}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Accès rapide */}
        <div className="px-2 py-3 border-b border-gray-200">
          <p className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Accès rapide
          </p>
          <div className="space-y-0.5">
            <Link
              to="/cockpit-pro/messagerie"
              onClick={onClose}
              className={`
                flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all
                ${location.pathname === '/cockpit-pro/messagerie' 
                  ? 'bg-gray-100 text-gray-900 font-medium' 
                  : 'text-gray-700 hover:bg-gray-100'
                }
              `}
            >
              <MessageCircle size={18} className="text-gray-500" />
              <span>Messagerie</span>
            </Link>
            <Link
              to="/cockpit-pro/collaboration/taches"
              onClick={onClose}
              className={`
                flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all
                ${location.pathname === '/cockpit-pro/collaboration/taches' 
                  ? 'bg-gray-100 text-gray-900 font-medium' 
                  : 'text-gray-700 hover:bg-gray-100'
                }
              `}
            >
              <CheckCircle size={18} className="text-gray-500" />
              <span>Tâches</span>
            </Link>
            <Link
              to="/cockpit-pro/commercial/devis"
              onClick={onClose}
              className={`
                flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all
                ${location.pathname === '/cockpit-pro/commercial/devis' 
                  ? 'bg-gray-100 text-gray-900 font-medium' 
                  : 'text-gray-700 hover:bg-gray-100'
                }
              `}
            >
              <FileText size={18} className="text-gray-500" />
              <span>Devis</span>
            </Link>
            
            {/* Bouton Notes Vocales - Superpouvoir */}
            <button
              onClick={() => {
                onClose();
                if (onOpenVoiceNotes) onOpenVoiceNotes();
              }}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all text-gray-700 hover:bg-gradient-to-r hover:from-[#F97316]/10 hover:to-[#F97316]/5 hover:text-[#F97316] group"
              data-testid="sidebar-voice-notes-btn"
            >
              <div className="relative">
                <Mic size={18} className="text-gray-500 group-hover:text-[#F97316]" />
                <Sparkles size={10} className="absolute -top-1 -right-1 text-[#F97316]" />
              </div>
              <span>Notes vocales</span>
              <span className="ml-auto px-1.5 py-0.5 bg-gradient-to-r from-[#F97316] to-[#ea6c0a] text-white text-[10px] font-bold rounded uppercase">
                Pro
              </span>
            </button>
            
            {/* Lien Abra IA - Super Assistant avec Artifacts */}
            <Link
              to="/cockpit-pro/abra-ia"
              onClick={onClose}
              className={`
                flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all
                ${location.pathname === '/cockpit-pro/abra-ia' 
                  ? 'bg-gradient-to-r from-[#1E3A5F]/10 to-[#1E3A5F]/5 text-[#1E3A5F] font-medium' 
                  : 'text-gray-700 hover:bg-gradient-to-r hover:from-[#1E3A5F]/10 hover:to-[#1E3A5F]/5 hover:text-[#1E3A5F]'
                }
              `}
              data-testid="sidebar-abra-ia-btn"
            >
              <div className="relative">
                <Sparkles size={18} className={location.pathname === '/cockpit-pro/abra-ia' ? 'text-[#1E3A5F]' : 'text-gray-500'} />
              </div>
              <span>Abra IA</span>
              <span className="ml-auto px-1.5 py-0.5 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] text-white text-[10px] font-bold rounded uppercase">
                New
              </span>
            </Link>
          </div>
        </div>

        {/* Navigation par espaces */}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          <p className="px-3 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Mes Espaces
          </p>
          
          {Object.values(ESPACES_PRO_CONFIG)
            .filter(espace => !espace.superadminOnly || isSuperAdmin)
            .map((espace) => (
            <EspaceMenuItem
              key={espace.id}
              espace={espace}
              isExpanded={expandedEspaces.includes(espace.id)}
              onToggle={() => toggleEspace(espace.id)}
              onClose={onClose}
              location={location}
              onShowComingSoon={setComingSoonModal}
            />
          ))}
          
          {/* Badge Superadmin */}
          {isSuperAdmin && (
            <div className="mx-3 mt-4 p-3 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl">
              <div className="flex items-center gap-2 text-white">
                <Shield size={18} />
                <span className="text-sm font-bold">Mode Superadmin</span>
              </div>
              <p className="text-xs text-white/80 mt-1">
                Accès complet à la plateforme
              </p>
            </div>
          )}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <Link 
            to="/cockpit-pro/admin/abonnement"
            className="flex items-center gap-2 px-3 py-2 mb-2 text-[#F97316] bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors text-sm font-medium"
          >
            <Zap size={16} />
            <span>Passer à {subscription === 'artisan' ? 'Pro' : 'Business'}</span>
          </Link>
          <button 
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
          >
            <LogOut size={16} />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Modal "À venir" */}
      <AnimatePresence>
        {comingSoonModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[10300] flex items-center justify-center p-4"
            onClick={() => setComingSoonModal(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header avec icône */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] flex items-center justify-center shadow-lg">
                  {comingSoonModal.icon && <comingSoonModal.icon className="w-7 h-7 text-white" />}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{comingSoonModal.label}</h3>
                  <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                    En cours de développement
                  </span>
                </div>
              </div>
              
              {/* Description */}
              <p className="text-gray-700 font-medium mb-2">
                Arrive bientôt !
              </p>
              <p className="text-gray-500 text-sm mb-6">
                {comingSoonModal.comingSoonDescription}
              </p>
              
              {/* CTA */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="text-sm text-gray-600 text-center">
                  Tu veux être informé du lancement ?
                </p>
                <p className="text-xs text-gray-400 text-center mt-1">
                  En tant que membre, tu seras notifié en priorité.
                </p>
              </div>
              
              {/* Bouton fermer */}
              <button
                onClick={() => setComingSoonModal(null)}
                className="w-full py-3 bg-[#1E3A5F] text-white rounded-xl font-semibold hover:bg-[#2d5a8a] transition-colors"
                data-testid="coming-soon-close-btn"
              >
                J'ai compris
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PAGE PRINCIPALE
// ══════════════════════════════════════════════════════════════════════════════

// Ancien écran d'invitation (cockpit fermé aux visiteurs) — conservé au cas où,
// plus affiché depuis l'ouverture du cockpit pro à tous (décision Romain 12/06)
// eslint-disable-next-line no-unused-vars
function CockpitProInvitation() {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 text-center"
      style={{ background: 'linear-gradient(180deg, #1E3A5F 0%, #152a45 100%)' }}
    >
      <img
        src="/icons/abracada-logo.svg"
        alt="Abracada Pro"
        className="w-20 h-20 rounded-3xl mb-6 shadow-lg"
      />
      <h1
        className="text-2xl font-bold text-white mb-2"
        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
      >
        Bienvenue dans Abracada Pro
      </h1>
      <p className="text-white/70 text-sm max-w-sm mb-8">
        Le centre de pilotage de ta boutique : commandes, clients, devis,
        chantiers, marketing — épaulé par tes agents IA.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xs sm:max-w-none sm:w-auto">
        <Link
          to="/connexion"
          className="px-6 py-3 rounded-xl text-white font-semibold shadow-md text-center"
          style={{ background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)' }}
        >
          Se connecter
        </Link>
        <Link
          to="/pro"
          className="px-6 py-3 rounded-xl font-semibold text-white text-center border border-white/30 hover:bg-white/10 transition-colors"
        >
          Découvrir et louer ma boutique
        </Link>
      </div>
      <Link to="/" className="mt-6 text-white/50 text-sm hover:text-white/80">
        ← Retour à l'accueil
      </Link>
    </div>
  );
}

export default function CockpitProLayout() {
  // Design unifié ChatGPT/Claude pour Desktop ET Mobile
  // Le composant CockpitAssistantUnified gère lui-même le responsive

  // Cockpit pro OUVERT À TOUS (décision Romain 12/06) : le visiteur entre et
  // se balade partout — c'est la meilleure vitrine. Sans compte : barre du
  // haut « Se connecter / S'inscrire », données démo, et toute action qui
  // FAIT quelque chose (chat Abra…) ouvre la pop-up d'inscription.
  // Même principe que le cockpit particulier, ouvert le 11/06.
  return <CockpitAssistantUnified userType="pro" />;
}

// Layout mobile classique (ancien CockpitProLayout)
function CockpitProMobileLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showVoiceNotes, setShowVoiceNotes] = useState(false);
  const [assistantMessage, setAssistantMessage] = useState('');
  const [showAssistantChat, setShowAssistantChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [isAssistantTyping, setIsAssistantTyping] = useState(false);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [selectedMessageContent, setSelectedMessageContent] = useState('');
  const [sendMode, setSendMode] = useState(null); // 'message' | 'email'
  const [crmContacts, setCrmContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [showArtifactsPanel, setShowArtifactsPanel] = useState(false);
  const [activeArtifact, setActiveArtifact] = useState(null);
  const [showArtifactViewer, setShowArtifactViewer] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sellerProfile, setSellerProfile] = useState(null);
  // États pour l'upload de fichiers
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Vérifier l'authentification et charger le profil
  // MODE DÉMO : Si pas de token, utiliser des données fictives
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('client_token');
      const storedUser = localStorage.getItem('client_user');

      // MODE DÉMO : Permettre l'accès sans authentification
      if (!token || !storedUser) {
        // Créer un utilisateur démo SUPERADMIN (pour tester la plateforme)
        setUser({
          id: 'demo_pro_user',
          name: 'AbracadaWorld Admin',
          email: 'admin@abracadaworld.com',
          role: 'superadmin'  // Accès complet à la plateforme
        });
        setSellerProfile({
          shop_name: 'AbracadaWorld',
          activity_type: 'Plateforme',
          average_rating: 5,
          products_count: 0,
          team_count: 1,
          subscription: 'premium',
          shop_slug: 'abracadaworld'
        });
        setLoading(false);
        return;
      }

      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);

        // Charger le profil vendeur/artisan
        const profileResponse = await fetch(`${API_URL}/api/seller/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          if (profileData.has_profile && profileData.seller) {
            setSellerProfile(profileData.seller);
          }
        }
      } catch (error) {
        console.error('Auth error:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  // Fermer sidebar sur changement de page
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Déconnexion
  const handleLogout = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_user');
    localStorage.removeItem('buyer_token');
    localStorage.removeItem('ecosystem_token');
    window.location.href = '/';
  };

  // Profil utilisateur (données réelles ou par défaut)
  const userProfile = {
    name: sellerProfile?.shop_name || user?.name || 'Mon Entreprise',
    initials: (sellerProfile?.shop_name || user?.name || 'ME').slice(0, 2).toUpperCase(),
    metier: sellerProfile?.activity_type || 'Artisan',
    rating: sellerProfile?.average_rating?.toFixed(1) || '4.5',
    productCount: sellerProfile?.products_count || 0,
    teamCount: sellerProfile?.team_count || 1,
    email: user?.email || '',
    id: user?.id || sellerProfile?.user_id || '',
    shopSlug: sellerProfile?.shop_slug || '',
  };

  // Abonnement (à récupérer depuis le profil)
  const subscription = sellerProfile?.subscription || 'artisan';

  // ═══════════════════════════════════════════════════════════════════════════
  // FONCTIONS ASSISTANT CHAT - CONNEXION CRM
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Charger les contacts CRM
  const fetchCrmContacts = async () => {
    setContactsLoading(true);
    try {
      const token = localStorage.getItem('client_token') || localStorage.getItem('ecosystem_token');
      const res = await fetch(`${API_URL}/api/crm/contacts/all`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (res.ok) {
        const data = await res.json();
        setCrmContacts(data.contacts || []);
      } else {
        // Fallback démo
        setCrmContacts([
          { id: 'demo-1', name: 'Jean Dupont', email: 'jean.dupont@email.fr', type: 'client' },
          { id: 'demo-2', name: 'Marie Martin', email: 'marie.martin@email.fr', type: 'prospect' },
          { id: 'demo-3', name: 'Pierre Durand', email: 'pierre.durand@email.fr', phone: '06 12 34 56 78', type: 'client' },
        ]);
      }
    } catch (e) {
      console.error('Erreur chargement contacts:', e);
      setCrmContacts([
        { id: 'demo-1', name: 'Jean Dupont', email: 'jean.dupont@email.fr', type: 'client' },
        { id: 'demo-2', name: 'Marie Martin', email: 'marie.martin@email.fr', type: 'prospect' },
      ]);
    } finally {
      setContactsLoading(false);
    }
  };

  // Envoyer le message à un contact via l'API CRM
  const handleSendToContact = async (contact) => {
    if (sendMode === 'email') {
      // Ouvrir mailto avec le contenu
      const subject = encodeURIComponent('Message de votre artisan');
      const body = encodeURIComponent(selectedMessageContent);
      window.open(`mailto:${contact.email}?subject=${subject}&body=${body}`);
      setShowContactPicker(false);
    } else {
      // Envoyer via API AbracadaChat
      try {
        const token = localStorage.getItem('client_token') || localStorage.getItem('ecosystem_token');
        const res = await fetch(`${API_URL}/api/crm/contacts/${contact.id}/send-message?message=${encodeURIComponent(selectedMessageContent)}`, {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (res.ok) {
          const data = await res.json();
          alert(`✅ Message envoyé à ${contact.name} !`);
        } else {
          alert(`Message envoyé à ${contact.name} (mode démo)`);
        }
      } catch (e) {
        console.error('Erreur envoi message:', e);
        alert(`Message envoyé à ${contact.name} (mode démo)`);
      }
      setShowContactPicker(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // FONCTIONS ASSISTANT CHAT AVEC ARTIFACTS
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Envoyer un message à l'assistant - CONNEXION API RÉELLE
  const handleSendAssistantMessage = async () => {
    if (!assistantMessage.trim()) return;
    
    const userMessage = assistantMessage.trim();
    
    // Ajouter le message utilisateur
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setShowAssistantChat(true);
    setIsAssistantTyping(true);
    setAssistantMessage('');
    
    try {
      const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token');
      const userId = (() => {
        const ecosystemUser = localStorage.getItem('ecosystem_user');
        if (ecosystemUser) {
          try { return JSON.parse(ecosystemUser).id; } catch(e) {}
        }
        return localStorage.getItem('userId') || 'demo_user';
      })();
      
      // Appel API réel à l'assistant IA
      const res = await fetch(`${API_URL}/api/assistant/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          message: userMessage,
          user_id: userId,
          context: {
            user_type: 'pro',
            platform: 'mobile'
          }
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        
        // Construire l'artifact si présent dans la réponse
        let artifact = null;
        if (data.has_artifact && data.artifact) {
          artifact = {
            type: data.artifact.type,
            title: data.artifact.title || 'Résultat',
            subtitle: data.artifact.subtitle || '',
            preview: data.artifact.preview || '',
            data: data.artifact.data || data.artifact
          };
        }
        
        setChatMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.response || data.message,
          artifact: artifact
        }]);
      } else {
        throw new Error('Erreur API');
      }
    } catch (e) {
      console.error('Erreur assistant:', e);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Désolé, je rencontre un problème technique. Réessaie dans quelques instants.'
      }]);
    } finally {
      setIsAssistantTyping(false);
    }
  };

  // Ouvrir un artifact
  const handleOpenArtifact = (artifact) => {
    setActiveArtifact(artifact);
    setShowArtifactViewer(true);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLERS UPLOAD DE FICHIERS (Mobile)
  // ═══════════════════════════════════════════════════════════════════════════
  
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setIsUploading(true);
    setShowAttachMenu(false);
    
    const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token');
    
    for (const file of files) {
      try {
        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');
        const isPDF = file.type === 'application/pdf';
        
        // Upload le fichier
        const formData = new FormData();
        formData.append('file', file);
        
        const uploadRes = await fetch(`${API_URL}/api/upload/image`, {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData
        });
        
        if (uploadRes.ok) {
          const uploadData = await uploadRes.json();
          
          const newFile = {
            id: uploadData.id || `file-${Date.now()}`,
            name: file.name,
            url: uploadData.url || uploadData.image_url,
            type: isImage ? 'image' : isVideo ? 'video' : isPDF ? 'pdf' : 'document',
          };
          
          setUploadedFiles(prev => [...prev, newFile]);
          
          // Créer le message avec analyse contextuelle
          let analysisPrompt = `J'ai uploadé un fichier : "${file.name}"`;
          const fileName = file.name.toLowerCase();
          
          if (isImage) {
            if (fileName.includes('devis')) {
              analysisPrompt = `J'ai uploadé une image d'un devis : "${file.name}". Peux-tu l'analyser ?`;
            } else if (fileName.includes('facture')) {
              analysisPrompt = `J'ai uploadé une image d'une facture : "${file.name}". Peux-tu l'analyser ?`;
            } else if (fileName.includes('chantier')) {
              analysisPrompt = `J'ai uploadé une photo de chantier : "${file.name}". Que vois-tu ?`;
            } else {
              analysisPrompt = `J'ai uploadé cette image : "${file.name}". Peux-tu me dire ce que tu vois ?`;
            }
          } else if (isPDF) {
            if (fileName.includes('devis')) {
              analysisPrompt = `J'ai uploadé un devis PDF : "${file.name}". Peux-tu l'analyser ?`;
            } else if (fileName.includes('facture')) {
              analysisPrompt = `J'ai uploadé une facture PDF : "${file.name}". Peux-tu l'analyser ?`;
            } else {
              analysisPrompt = `J'ai uploadé ce document PDF : "${file.name}". Peux-tu l'analyser ?`;
            }
          }
          
          // Ajouter le message utilisateur avec l'attachment
          setChatMessages(prev => [...prev, { 
            role: 'user', 
            content: analysisPrompt,
            attachment: newFile
          }]);
          setShowAssistantChat(true);
          setIsAssistantTyping(true);
          
          // Envoyer à l'IA pour analyse
          const userId = (() => {
            const ecosystemUser = localStorage.getItem('ecosystem_user');
            if (ecosystemUser) {
              try { return JSON.parse(ecosystemUser).id; } catch(e) {}
            }
            return 'demo_user';
          })();
          
          const analysisRes = await fetch(`${API_URL}/api/assistant/chat`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
              message: analysisPrompt,
              user_id: userId,
              context: {
                user_type: 'pro',
                platform: 'mobile',
                attachment: {
                  type: newFile.type,
                  url: newFile.url,
                  name: newFile.name
                }
              }
            })
          });
          
          if (analysisRes.ok) {
            const data = await analysisRes.json();
            setChatMessages(prev => [...prev, { 
              role: 'assistant', 
              content: data.response || data.message
            }]);
          }
        }
      } catch (error) {
        console.error('Erreur upload:', error);
      }
    }
    
    setIsUploading(false);
    setUploadedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Actions sur un artifact
  const handleArtifactAction = (action, artifact) => {
    switch (action) {
      case 'download':
        console.log('Télécharger:', artifact);
        break;
      case 'share':
        console.log('Partager:', artifact);
        // TODO: Ouvrir modal d'envoi
        break;
      case 'edit':
        console.log('Modifier:', artifact);
        break;
      default:
        break;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#1E3A5F] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement du Cockpit Pro...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <CockpitProSidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
        userProfile={userProfile}
        subscription={subscription}
        onLogout={handleLogout}
        onOpenVoiceNotes={() => setShowVoiceNotes(true)}
        userRole={user?.role}
      />

      <div className="flex-1 flex flex-col min-h-screen bg-white dark:bg-slate-900 transition-colors">
        {/* Top Bar Mobile - FIXÉ SOUS le TopBanner (64px header + 30px beta = 94px) */}
        <header
          className="lg:hidden bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-700 px-4 py-2 fixed left-0 right-0 z-30"
          style={{ top: '94px' }}
        >
          <div className="flex items-center justify-between">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-3 -ml-2 hover:bg-gray-100 dark:hover:bg-slate-700 active:bg-gray-200 rounded-xl transition-colors"
              aria-label="Menu"
            >
              <Menu size={22} className="text-gray-700 dark:text-slate-300" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8f] rounded-lg flex items-center justify-center">
                <Briefcase size={16} className="text-white" />
              </div>
              <span className="text-base font-bold text-gray-900 dark:text-white">Cockpit Pro</span>
            </div>
            <div className="flex items-center gap-1">
              <AbracaCoinsBadge />
              <ThemeToggle />
              <button
                onClick={() => setShowArtifactsPanel(true)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 active:bg-gray-200 rounded-xl transition-all"
                aria-label="Mes documents"
                data-testid="artifacts-button-pro"
              >
                <PanelRightOpen size={22} className="text-[#1E3A5F] dark:text-slate-300" />
              </button>
              <NotificationsBell token={localStorage.getItem('client_token')} accentColor="#E8650A" />
              <NotificationBell 
                onClick={() => setShowNotifications(true)}
                className="hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl"
              />
            </div>
          </div>
        </header>

        {/* Top Bar Desktop */}
        <header className="hidden lg:flex bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-6 py-3 items-center justify-between sticky top-0 z-30">
          {/* Search */}
          <div className="flex flex-1 max-w-md">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                className="w-full pl-10 pr-4 py-2 bg-gray-100 border-0 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            {/* Credit Badge */}
            <AbracaCoinsBadge />
            
            {/* Dark Mode Toggle */}
            <ThemeToggle />
            
            {/* Publier */}
            <button 
              onClick={() => setShowPublishModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#F97316] hover:bg-[#ea6c0a] text-white font-semibold rounded-xl transition-colors"
            >
              <PlusCircle size={18} />
              <span>Publier</span>
            </button>
            
            {/* Notifications */}
            <NotificationsBell token={localStorage.getItem('client_token')} accentColor="#E8650A" />
            <NotificationBell 
              onClick={() => setShowNotifications(true)}
              className="hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl"
            />
            
            {/* Voir ma boutique */}
            <Link 
              to={userProfile?.shopSlug ? `/boutique/${userProfile.shopSlug}` : "/cockpit-pro/boutique"}
              className="flex items-center gap-2 px-4 py-2 border border-[#1E3A5F] dark:border-slate-500 text-[#1E3A5F] dark:text-slate-200 font-semibold rounded-xl hover:bg-[#1E3A5F]/5 dark:hover:bg-slate-700 transition-colors"
            >
              <Eye size={18} />
              <span>Voir ma boutique</span>
            </Link>
            
            {/* Déconnexion - Visible */}
            <button 
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 font-medium rounded-xl transition-colors border border-red-200 dark:border-red-800"
              data-testid="header-logout-btn-pro"
            >
              <LogOut size={18} />
              <span>Déconnexion</span>
            </button>
          </div>
        </header>
        
        {/* Menu de navigation principal - Desktop uniquement */}
        <CockpitProTopNav />

        {/* ═══ BARRE DE CHAT SUPER ASSISTANT (Mobile uniquement) ═══ */}
        <div 
          className="lg:hidden fixed left-0 right-0"
          style={{ 
            bottom: 'calc(60px + env(safe-area-inset-bottom))',
            zIndex: 10099,
          }}
        >
          {/* Zone de chat expandable */}
          <AnimatePresence>
            {showAssistantChat && chatMessages.length > 0 && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mx-3 mb-2 bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
                style={{ maxHeight: '300px' }}
              >
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                  <span className="text-xs font-semibold text-[#1E3A5F]">Assistant</span>
                  <button 
                    onClick={() => setShowAssistantChat(false)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <X size={14} className="text-gray-400" />
                  </button>
                </div>
                <div className="p-3 overflow-y-auto" style={{ maxHeight: '240px' }}>
                  {chatMessages.map((msg, idx) => (
                    <div key={idx} className={`mb-3 ${msg.role === 'user' ? 'text-right' : ''}`}>
                      {msg.role === 'assistant' ? (
                        <div>
                          <div className="inline-block bg-gray-100 rounded-xl rounded-tl-sm px-3 py-2 text-sm text-gray-800 max-w-[90%] text-left">
                            {msg.content}
                          </div>
                          
                          {/* Carte Artifact cliquable (style Claude) */}
                          {msg.artifact && (
                            <ArtifactCard 
                              artifact={msg.artifact}
                              onClick={handleOpenArtifact}
                            />
                          )}
                          
                          {/* Boutons d'action : Copier / Message / Mail */}
                          <div className="flex items-center gap-1 mt-1">
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(msg.content);
                              }}
                              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors group"
                              title="Copier"
                              data-testid="chat-action-copy-pro"
                            >
                              <Copy size={14} className="text-gray-400 group-hover:text-[#1E3A5F]" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedMessageContent(msg.content);
                                setSendMode('message');
                                setShowContactPicker(true);
                                fetchCrmContacts();
                              }}
                              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors group"
                              title="Envoyer par message"
                              data-testid="chat-action-message-pro"
                            >
                              <MessageCircle size={14} className="text-gray-400 group-hover:text-[#1E3A5F]" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedMessageContent(msg.content);
                                setSendMode('email');
                                setShowContactPicker(true);
                                fetchCrmContacts();
                              }}
                              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors group"
                              title="Envoyer par email"
                              data-testid="chat-action-email-pro"
                            >
                              <Mail size={14} className="text-gray-400 group-hover:text-[#1E3A5F]" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="inline-block bg-[#1E3A5F] text-white rounded-xl rounded-tr-sm px-3 py-2 text-sm max-w-[85%]">
                          {msg.content}
                        </div>
                      )}
                    </div>
                  ))}
                  {isAssistantTyping && (
                    <div className="flex items-center gap-1 text-gray-400 text-sm">
                      <div className="flex gap-1">
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Barre de saisie compacte */}
          <div 
            className="mx-3 mb-2 rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
            style={{ background: 'white' }}
          >
            {/* Fichiers uploadés en attente */}
            {uploadedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 px-3 pt-2">
                {uploadedFiles.map(file => (
                  <div key={file.id} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-lg text-xs">
                    {file.type === 'image' && <ImageIcon size={12} className="text-blue-500" />}
                    {file.type === 'video' && <Film size={12} className="text-purple-500" />}
                    {file.type === 'pdf' && <FileText size={12} className="text-red-500" />}
                    {file.type === 'document' && <File size={12} className="text-green-500" />}
                    <span className="truncate max-w-[100px]">{file.name}</span>
                    <button onClick={() => setUploadedFiles(prev => prev.filter(f => f.id !== file.id))}>
                      <X size={12} className="text-gray-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex items-center gap-2 p-2">
              {/* Bouton + pour ajouter des fichiers */}
              <div className="relative">
                <button
                  onClick={() => setShowAttachMenu(!showAttachMenu)}
                  disabled={isUploading}
                  className={`p-2 rounded-xl transition-colors ${
                    showAttachMenu 
                      ? 'bg-[#E8650A] text-white' 
                      : 'hover:bg-gray-100 text-[#1E3A5F]'
                  }`}
                >
                  {isUploading ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Plus size={18} />
                  )}
                </button>
                
                {/* Menu dropdown pour les attachments */}
                <AnimatePresence>
                  {showAttachMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50"
                    >
                      <div className="p-2">
                        <p className="px-2 py-1 text-xs font-medium text-gray-400 uppercase">
                          Importer
                        </p>
                        <button
                          onClick={() => {
                            fileInputRef.current?.setAttribute('accept', 'image/*');
                            fileInputRef.current?.click();
                          }}
                          className="w-full flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-lg"
                        >
                          <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
                            <ImageIcon size={14} className="text-blue-600" />
                          </div>
                          <span className="text-sm text-gray-800">Photos</span>
                        </button>
                        <button
                          onClick={() => {
                            fileInputRef.current?.setAttribute('accept', '.pdf,.doc,.docx');
                            fileInputRef.current?.click();
                          }}
                          className="w-full flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-lg"
                        >
                          <div className="w-7 h-7 rounded-lg bg-red-100 flex items-center justify-center">
                            <FileText size={14} className="text-red-600" />
                          </div>
                          <span className="text-sm text-gray-800">Documents</span>
                        </button>
                        <button
                          onClick={() => {
                            fileInputRef.current?.setAttribute('accept', 'video/*');
                            fileInputRef.current?.click();
                          }}
                          className="w-full flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-lg"
                        >
                          <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center">
                            <Film size={14} className="text-purple-600" />
                          </div>
                          <span className="text-sm text-gray-800">Vidéos</span>
                        </button>
                        <button
                          onClick={() => {
                            fileInputRef.current?.setAttribute('accept', '*/*');
                            fileInputRef.current?.click();
                          }}
                          className="w-full flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-lg"
                        >
                          <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">
                            <Paperclip size={14} className="text-gray-600" />
                          </div>
                          <span className="text-sm text-gray-800">Autres</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {/* Input file caché */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>
              
              {/* Input */}
              <input
                type="text"
                value={assistantMessage}
                onChange={(e) => setAssistantMessage(e.target.value)}
                placeholder="Demande à ton assistant..."
                className="flex-1 bg-gray-100 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E8650A] focus:bg-white transition-all"
                data-testid="assistant-input-pro"
                disabled={isUploading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && assistantMessage.trim()) {
                    handleSendAssistantMessage();
                  }
                }}
              />
              
              {/* Bouton Micro */}
              <button 
                onClick={() => setShowVoiceNotes(true)}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <Mic size={18} className="text-[#1E3A5F]" />
              </button>
              
              {/* Bouton Envoyer */}
              <button 
                className={`p-2 rounded-xl transition-all ${
                  assistantMessage.trim() 
                    ? 'bg-[#E8650A] hover:bg-[#d45a09]' 
                    : 'bg-gray-200'
                }`}
                onClick={handleSendAssistantMessage}
                disabled={isUploading}
                data-testid="assistant-send-btn-pro"
              >
                <Send size={16} className={assistantMessage.trim() ? 'text-white' : 'text-gray-400'} />
              </button>
            </div>
          </div>
        </div>

        {/* Modal de sélection de contact */}
        <AnimatePresence>
          {showContactPicker && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[10200] flex items-end justify-center"
              onClick={() => setShowContactPicker(false)}
            >
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="w-full max-w-lg bg-white rounded-t-2xl p-4"
                style={{ maxHeight: '60vh' }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-[#1E3A5F]">
                    {sendMode === 'email' ? 'Envoyer par email à...' : 'Envoyer un message à...'}
                  </h3>
                  <button onClick={() => setShowContactPicker(false)} className="p-1">
                    <X size={20} className="text-gray-400" />
                  </button>
                </div>
                
                {/* Aperçu du message */}
                <div className="bg-gray-100 rounded-xl p-3 mb-4 text-sm text-gray-600 max-h-20 overflow-y-auto">
                  {selectedMessageContent.substring(0, 150)}{selectedMessageContent.length > 150 ? '...' : ''}
                </div>
                
                {/* Liste des contacts */}
                <div className="space-y-2 overflow-y-auto" style={{ maxHeight: '200px' }}>
                  <p className="text-xs text-gray-400 uppercase font-semibold mb-2">Contacts récents</p>
                  
                  {contactsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="w-6 h-6 border-2 border-[#1E3A5F] border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : crmContacts.length > 0 ? (
                    crmContacts.map(contact => (
                      <button
                        key={contact.id}
                        onClick={() => handleSendToContact(contact)}
                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors"
                        data-testid={`contact-pro-${contact.id}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white font-semibold">
                          {contact.name?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-medium text-gray-800">{contact.name}</p>
                          <p className="text-xs text-gray-500">{contact.email || contact.phone || 'Pas de contact'}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded ${contact.type === 'client' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                          {contact.type === 'client' ? 'Client' : 'Prospect'}
                        </span>
                      </button>
                    ))
                  ) : (
                    <p className="text-center text-gray-500 py-4">Aucun contact trouvé</p>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile Bottom Navigation - Style identique au Particulier : fond bleu #1E3A5F */}
        {/* z-index très élevé pour rester AU-DESSUS des menus/drawers */}
        <nav 
          className="lg:hidden fixed bottom-0 left-0 right-0"
          style={{ 
            background: '#1E3A5F',
            height: '60px',
            paddingBottom: 'env(safe-area-inset-bottom)',
            borderTop: '2px solid #E8650A',
            zIndex: 10100,
          }}
          data-testid="cockpit-pro-mobile-bottom-nav"
        >
          <div className="grid grid-cols-5 h-full items-center">
            {/* Accueil */}
            <Link 
              to="/cockpit-pro" 
              className="flex flex-col items-center justify-center gap-[2px] h-full"
              data-testid="cockpit-pro-nav-accueil"
            >
              <Home 
                size={22} 
                strokeWidth={2}
                color={location.pathname === '/cockpit-pro' ? '#ffffff' : 'rgba(255,255,255,0.7)'}
              />
              <span 
                style={{ 
                  color: location.pathname === '/cockpit-pro' ? '#ffffff' : 'rgba(255,255,255,0.7)',
                  fontSize: '9px',
                  fontWeight: location.pathname === '/cockpit-pro' ? 700 : 600,
                  fontFamily: "'DM Sans', sans-serif",
                  letterSpacing: '0.01em',
                }}
              >
                Accueil
              </span>
            </Link>
            
            {/* Devis */}
            <Link 
              to="/cockpit-pro/commercial/demandes-devis" 
              className="flex flex-col items-center justify-center gap-[2px] h-full relative"
              data-testid="cockpit-pro-nav-devis"
            >
              <div className="relative">
                <Target 
                  size={22} 
                  strokeWidth={2}
                  color={location.pathname.includes('/commercial') ? '#ffffff' : 'rgba(255,255,255,0.7)'}
                />
                <span className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] bg-[#E8650A] text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5 shadow">
                  8
                </span>
              </div>
              <span 
                style={{ 
                  color: location.pathname.includes('/commercial') ? '#ffffff' : 'rgba(255,255,255,0.7)',
                  fontSize: '9px',
                  fontWeight: location.pathname.includes('/commercial') ? 700 : 600,
                  fontFamily: "'DM Sans', sans-serif",
                  letterSpacing: '0.01em',
                }}
              >
                Devis
              </span>
            </Link>
            
            {/* Publier (FAB central) - ORANGE pour ressortir sur fond bleu */}
            <button 
              onClick={() => setShowPublishModal(true)}
              className="flex flex-col items-center justify-end gap-[2px] h-full pb-[6px]"
              data-testid="cockpit-pro-nav-publier"
            >
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg -mt-10"
                style={{ 
                  background: 'linear-gradient(135deg, #E8650A 0%, #F97316 100%)',
                  boxShadow: '0 4px 12px rgba(232, 101, 10, 0.4)'
                }}
              >
                <PlusCircle size={24} color="white" strokeWidth={2} />
              </div>
              <span 
                style={{ 
                  color: '#ffffff',
                  fontSize: '9px',
                  fontWeight: 700,
                  fontFamily: "'DM Sans', sans-serif",
                  letterSpacing: '0.01em',
                }}
              >
                Publier
              </span>
            </button>
            
            {/* Équipe */}
            <Link 
              to="/cockpit-pro/collaboration/tchat" 
              className="flex flex-col items-center justify-center gap-[2px] h-full relative"
              data-testid="cockpit-pro-nav-equipe"
            >
              <div className="relative">
                <MessageCircle 
                  size={22} 
                  strokeWidth={2}
                  color={location.pathname.includes('/collaboration') ? '#ffffff' : 'rgba(255,255,255,0.7)'}
                />
                <span className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] bg-[#E8650A] text-white text-[9px] font-bold rounded-full flex items-center justify-center px-0.5 shadow">
                  3
                </span>
              </div>
              <span 
                style={{ 
                  color: location.pathname.includes('/collaboration') ? '#ffffff' : 'rgba(255,255,255,0.7)',
                  fontSize: '9px',
                  fontWeight: location.pathname.includes('/collaboration') ? 700 : 600,
                  fontFamily: "'DM Sans', sans-serif",
                  letterSpacing: '0.01em',
                }}
              >
                Équipe
              </span>
            </Link>
            
            {/* Stats */}
            <Link 
              to="/cockpit-pro/analytics" 
              className="flex flex-col items-center justify-center gap-[2px] h-full"
              data-testid="cockpit-pro-nav-stats"
            >
              <BarChart3 
                size={22} 
                strokeWidth={2}
                color={location.pathname.includes('/analytics') || location.pathname.includes('/finances') ? '#ffffff' : 'rgba(255,255,255,0.7)'}
              />
              <span 
                style={{ 
                  color: location.pathname.includes('/analytics') || location.pathname.includes('/finances') ? '#ffffff' : 'rgba(255,255,255,0.7)',
                  fontSize: '9px',
                  fontWeight: location.pathname.includes('/analytics') || location.pathname.includes('/finances') ? 700 : 600,
                  fontFamily: "'DM Sans', sans-serif",
                  letterSpacing: '0.01em',
                }}
              >
                Stats
              </span>
            </Link>
          </div>
        </nav>

        {/* Main Content - padding mobile : TopBanner(94px) + header cockpit(~60px) + espacement */}
        <main
          className="flex-1 p-4 lg:p-6 lg:pt-6 lg:pb-6"
          style={{
            paddingTop: 'calc(158px + 0.5rem)',
            paddingBottom: 'calc(60px + 70px + env(safe-area-inset-bottom, 0px) + 16px)'
          }}
        >
          <Outlet context={{ 
            user: user,
            userProfile,
            subscription,
            sellerProfile,
            token: localStorage.getItem('client_token')
          }} />
        </main>
      </div>

      {/* Modal Publier pour les Pros */}
      <PublishProModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        userProfile={userProfile}
        subscription={subscription}
      />

      {/* Panel Notifications */}
      <NotificationsPanel
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />

      {/* Modal Notes Vocales - Superpouvoir */}
      <AnimatePresence>
        {showVoiceNotes && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowVoiceNotes(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <VoiceNotesSuperpouvoir 
                userId={user?.id}
                onClose={() => setShowVoiceNotes(false)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ PANNEAU ARTIFACTS PRO (Devis, Factures, Chantiers) ═══ */}
      <AnimatePresence>
        {showArtifactsPanel && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[10300]"
            onClick={() => setShowArtifactsPanel(false)}
          >
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header du panneau */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-[#1E3A5F] to-[#2d4a6f]">
                <h2 className="text-lg font-bold text-white">Mes Documents</h2>
                <button
                  onClick={() => setShowArtifactsPanel(false)}
                  className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                >
                  <X size={20} className="text-white" />
                </button>
              </div>

              {/* Contenu du panneau */}
              <div className="p-4 overflow-y-auto" style={{ height: 'calc(100% - 70px)' }}>
                {/* Section Devis */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <FileText size={16} />
                    Mes Devis
                  </h3>
                  <div className="space-y-2">
                    <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-800">Devis #2024-042 - M. Dupont</p>
                        <p className="text-xs text-gray-500">Rénovation SDB • 4 850€</p>
                      </div>
                      <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">En attente</span>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-800">Devis #2024-041 - Mme Martin</p>
                        <p className="text-xs text-gray-500">Cuisine complète • 12 300€</p>
                      </div>
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Accepté</span>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-800">Devis #2024-040 - SCI Immo</p>
                        <p className="text-xs text-gray-500">Plomberie immeuble • 8 500€</p>
                      </div>
                      <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full">Refusé</span>
                    </div>
                  </div>
                </div>

                {/* Section Factures */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Receipt size={16} />
                    Mes Factures
                  </h3>
                  <div className="space-y-2">
                    <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-800">Facture #F2024-028</p>
                        <p className="text-xs text-gray-500">M. Bernard • 3 200€</p>
                      </div>
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Payée</span>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-800">Facture #F2024-027</p>
                        <p className="text-xs text-gray-500">Mme Petit • 1 850€</p>
                      </div>
                      <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded-full">En attente</span>
                    </div>
                  </div>
                </div>

                {/* Section Chantiers */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <FolderOpen size={16} />
                    Chantiers en cours
                  </h3>
                  <div className="space-y-2">
                    <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                      <p className="font-medium text-gray-800">Rénovation Villa Leblanc</p>
                      <p className="text-xs text-gray-500">Démarré le 15/04 • 2 semaines restantes</p>
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                        <div className="bg-[#1E3A5F] h-1.5 rounded-full" style={{ width: '70%' }}></div>
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-xl border border-gray-200">
                      <p className="font-medium text-gray-800">Extension Garage Moreau</p>
                      <p className="text-xs text-gray-500">Début prévu le 28/04</p>
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                        <div className="bg-gray-400 h-1.5 rounded-full" style={{ width: '0%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section Historique Assistant */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Sparkles size={16} />
                    Historique Assistant
                  </h3>
                  <div className="space-y-2">
                    <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                      <p className="font-medium text-gray-800 text-sm">"Génère un devis pour M. Dupont"</p>
                      <p className="text-xs text-gray-500 mt-1">Aujourd'hui, 09:45</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                      <p className="font-medium text-gray-800 text-sm">"Rédige une relance pour la facture F2024-027"</p>
                      <p className="text-xs text-gray-500 mt-1">Hier, 16:20</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ ARTIFACT VIEWER (Plein écran style Claude) ═══ */}
      <ArtifactViewer
        artifact={activeArtifact}
        isOpen={showArtifactViewer}
        onClose={() => {
          setShowArtifactViewer(false);
          setActiveArtifact(null);
        }}
        onAction={handleArtifactAction}
      />
      
      {/* Notifications Abra (mascotte) */}
      <AbraNotificationContainer />
    </div>
  );
}
